/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCloseDrawer';

const ActionCloseDrawerManager: ActionManagerFn<'ActionCloseDrawer'> = (
  platform: Platform,
): ActionManager<'ActionCloseDrawer'> => {
  return {
    ...baseActionManager(platform, 'ActionCloseDrawer'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Drawer Item ID',
        type: 'ItemID',
        initialFilter: 'Drawer',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Drawer') {
            return `Item id '${value}' doesn't belong to a Drawer`;
          }
          return '';
        },
      },
    },
    name: 'Close Drawer',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'times' },
    description: `**Overview**<br/>
The Close Drawer action is a UI navigation component responsible for programmatically dismissing a specific "Drawer" element (a slide-out panel) within the interface. It allows for precise control over the visibility of auxiliary content areas, ensuring drawers can be closed based on user interaction or logic flow.<br/><br/>
This component includes built-in validation logic to ensure configuration integrity. It verifies that the target identifier provided corresponds to a valid Drawer component type within the application's registry, and returns an error if a mismatch occurs.

**Key Features**<br/>
* **Targeted Closure:** Operates on a specific component ID, enabling management of multiple drawers on a single page by targeting them individually.
* **Type Validation:** Uses validateItemId to confirm the selected targetItemId belongs to a component of type 'Drawer', preventing misconfiguration.
* **Registry Filtering:** Uses an initialFilter of 'Drawer' to streamline configuration by restricting the selection UI to relevant Drawer components.

**Properties**<br/>
* **Drawer Item ID (targetItemId):** An ItemID property that specifies which drawer to close.
  * *Validation:* Confirms the referenced item exists and item.type === 'Drawer'.
  * *Error Handling:* Returns "Item id '[value]' doesn't belong to a Drawer" if validation fails.

**Use Cases**<br/>
* **Cancel Operations:** Assign to a "Cancel" button inside a form drawer to discard changes and close the panel.
* **Workflow Completion:** Automatically close a details drawer after a "Save" or "Submit" action completes successfully.`,
  };
};

export default ActionCloseDrawerManager;
